import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Search, ChevronDown } from "lucide-react";

import ViewContainer from "../../../../components/layout/view/ViewContainer";
import ContentPanel from "../../../../components/layout/content/ContentPanel";
import ContentPanelHeader from "../../../../components/layout/content/ContentPanelHeader";

import "./GlobalSearchField.css";

const OPTIONS = [
  { value: "videos", label: "Videos" },
  { value: "pops", label: "Pops" },
  { value: "albums", label: "Albums" },
  { value: "streams", label: "Streams" },
  { value: "talents", label: "Talents" },
];

export default function GlobalSearchField() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dropdownRef = useRef(null);

  const search = searchParams.get("q") || "";
  const selectedType = searchParams.get("type") || "videos";

  const [type, setType] = useState(selectedType);
  const [value, setValue] = useState(search);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    setValue(search);
    setType(
      OPTIONS.some((option) => option.value === selectedType)
        ? selectedType
        : "videos",
    );
  }, [search, selectedType]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!dropdownRef.current?.contains(event.target)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    document.addEventListener("pointerdown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("pointerdown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  const handleSearchSubmit = (event) => {
    event.preventDefault();

    const query = value.trim();

    if (!query) return;

    setIsOpen(false);

    const params = new URLSearchParams({
      type,
      q: query,
    });

    navigate(`/search?${params.toString()}`);
  };

  const handleSelectCategory = (selected) => {
    setType(selected);
    setIsOpen(false);
  };

  const selectedLabel =
    OPTIONS.find((option) => option.value === type)?.label || "Videos";

  return (
    <ViewContainer>
      <div className="global-search-field__container">
        <form
          className="global-search-field"
          onSubmit={handleSearchSubmit}
          role="search"
        >
          <div className="global-search-field__dropdown" ref={dropdownRef}>
            <button
              type="button"
              className={`global-search-field__category ${
                isOpen ? "global-search-field__category--active" : ""
              }`}
              onClick={() => setIsOpen((previous) => !previous)}
              aria-expanded={isOpen}
              aria-haspopup="true"
            >
              <span>{selectedLabel}</span>

              <ChevronDown
                size={17}
                className={`global-search-field__chevron ${
                  isOpen ? "global-search-field__chevron--open" : ""
                }`}
              />
            </button>

            {isOpen && (
              <div className="global-search-field__menu">
                {OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    className={`global-search-field__option ${
                      type === option.value
                        ? "global-search-field__option--selected"
                        : ""
                    }`}
                    onClick={() => handleSelectCategory(option.value)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          <input
            type="search"
            className="global-search-field__input"
            value={value}
            onChange={(event) => setValue(event.target.value)}
            placeholder="Search Katch..."
            aria-label="Search Katch"
            maxLength={200}
            autoFocus
          />

          <button
            type="submit"
            className="global-search-field__submit"
            aria-label="Submit search"
            disabled={!value.trim()}
          >
            <Search size={20} />
          </button>
        </form>
      </div>
    </ViewContainer>
  );
}
